import {
  Button,
  Card,
  CardBody,
  CodeEditor,
  Input,
  Textarea,
} from "@omnitok/ui";
import {
  AlertTriangle,
  Database,
  FileCode,
  Play,
  Plus,
  RefreshCw,
  Save,
  Trash2,
} from "lucide-react";
import { Disclosure } from "@pervasivemind/admin-ui";
import { Spinner } from "@pervasivemind/bi-components";
import { useCallback, useEffect, useMemo, useState } from "react";

import type { UseBuilderDraft } from "@/hooks/useBuilderDraft";
import { useT } from "@/i18n";
import { duckdbApi, projectsApi } from "@/lib/builder-api";
import { defaultViewTarget } from "@/lib/primary-table";
import type { DuckDBColumnInfo } from "@/types";

interface DataTabProps {
  draft: UseBuilderDraft;
}

/**
 * Single data source declaration on a report. Phase 2 — replaces the
 * legacy ``report_config.source`` (singular object) with an array.
 * Each entry materializes into its own DuckDB table when the admin
 * clicks Apply.
 */
interface ReportSource {
  /** UI-stable id used for the side-rail key + recreate scoping. */
  name?: string;
  /** Materialization kind. Phase 1+2 default is ``view``. */
  kind?: string;
  /** SELECT body that gets wrapped in CREATE OR REPLACE TABLE AS. */
  sql?: string;
  /** DuckDB table name the SELECT lands on. */
  target?: string;
  /**
   * Phase 4 — markdown prose describing what this table is, for the
   * chat AI's context (replaces the legacy AI > Training > DDL slot).
   * Lives next to the SQL so editing them in lockstep is the natural
   * workflow.
   */
  description?: string;
  /** Legacy parquet kind fields — preserved for backwards compat. */
  glob?: string;
  file?: string;
  alias?: string;
  table?: string;
}

/**
 * Read ``report_config`` and return the canonical ``sources`` array.
 * Backwards-compat: when only the legacy singular ``source`` exists,
 * wraps it as ``[source]`` so the rest of the UI only sees the array
 * shape. Returns ``[]`` when nothing is declared yet (fresh draft).
 */
function readSources(report: Record<string, unknown>): ReportSource[] {
  const rawSources = report.sources;
  if (Array.isArray(rawSources) && rawSources.length > 0) {
    return rawSources.filter((s): s is ReportSource => Boolean(s) && typeof s === "object");
  }
  const single = report.source;
  if (single && typeof single === "object") {
    return [single as ReportSource];
  }
  return [];
}

/** Default name for a newly-added source. ``data_2`` / ``data_3`` etc. */
function nextSourceName(existing: ReportSource[], reportKey: string): string {
  const existingNames = new Set(existing.map((s) => s.name).filter(Boolean));
  for (let i = existing.length + 1; i < 100; i += 1) {
    const candidate = `${reportKey}_${i}`;
    if (!existingNames.has(candidate)) return candidate;
  }
  return `source_${Date.now()}`;
}

/** True when the editor holds text that is not in `draft.report` yet
 *  ("Save view SQL" not clicked). Compared trimmed where onSaveActive
 *  trims, so a stray trailing space does not block Apply forever. */
export function sourceEditsPending(
  local: { name: string; target: string; sql: string; description: string },
  active: { name?: string; target?: string; sql?: string; description?: string },
): boolean {
  return (
    local.name.trim() !== (active.name ?? "").trim() ||
    local.target.trim() !== (active.target ?? "").trim() ||
    local.sql.trim() !== (active.sql ?? "").trim() ||
    local.description !== (active.description ?? "")
  );
}

export function DataTab({ draft }: DataTabProps): JSX.Element {
  const { t } = useT();
  const sources = useMemo(
    () => readSources(draft.report as Record<string, unknown>),
    [draft.report],
  );

  // Side-rail selection: index into the sources array.
  const [activeIndex, setActiveIndex] = useState(0);
  useEffect(() => {
    if (activeIndex >= sources.length) {
      setActiveIndex(Math.max(0, sources.length - 1));
    }
  }, [activeIndex, sources.length]);

  const active = sources[activeIndex] ?? null;

  // The slug the server resolved for this draft (every single-draft
  // route returns it — routes/builder.py::_draft_out), exactly as the AI
  // tab reads it. No client-side lookup, no default: null means the
  // server found no project, and the backend refuses that loudly.
  const projectSlug = draft.draft?.project_slug;

  // ── Local editor state — mirrored from active source ────────────
  const [name, setName] = useState(active?.name ?? "");
  const [target, setTarget] = useState(active?.target ?? "");
  const [sql, setSql] = useState(active?.sql ?? "");
  const [description, setDescription] = useState(active?.description ?? "");
  useEffect(() => {
    setName(active?.name ?? "");
    setTarget(active?.target ?? "");
    setSql(active?.sql ?? "");
    setDescription(active?.description ?? "");
  }, [activeIndex, active?.name, active?.target, active?.sql, active?.description]);

  // Schema introspection — once a target is materialized we can
  // DESCRIBE + sample its rows so the admin sees what columns the
  // dashboard SQLs will see.
  const [columns, setColumns] = useState<DuckDBColumnInfo[] | null>(null);
  const [sample, setSample] = useState<Array<Record<string, unknown>> | null>(null);
  const [introspectError, setIntrospectError] = useState<string | null>(null);
  const [introspectLoading, setIntrospectLoading] = useState(false);

  const refresh = useCallback(async () => {
    const tableName = (active?.target ?? "").trim();
    if (!tableName) {
      setColumns(null);
      setSample(null);
      return;
    }
    setIntrospectLoading(true);
    setIntrospectError(null);
    try {
      // Iter 31 — pass projectSlug so introspection targets the
      // per-project DuckDB file (data/{slug}.duckdb), not the legacy
      // shared analytics.duckdb. Without it the panel falsely reported
      // "Table doesn't exist in DuckDB yet" for materialized view tables.
      const cols = await duckdbApi.columns(tableName, projectSlug);
      setColumns(cols);
      const rows = await duckdbApi.sample(tableName, 10, projectSlug);
      setSample(rows);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes("404") || msg.toLowerCase().includes("not found")) {
        setColumns(null);
        setSample(null);
      } else {
        setIntrospectError(msg);
      }
    } finally {
      setIntrospectLoading(false);
    }
    // projectSlug comes with the draft and only changes if the draft is
    // replaced.
  }, [active?.target, projectSlug]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // ── Mutators — persist changes back to draft.report.sources ─────
  const writeSources = (next: ReportSource[]): void => {
    // Patch both ``sources`` (Phase 2 canonical) and ``source`` (legacy
    // mirror) so older code paths reading the singular field still see
    // the active first source. ``source`` mirrors ``sources[0]``.
    const firstWithoutMeta: ReportSource = next[0]
      ? {
          ...next[0],
          // legacy clients don't need the new ``name`` / ``description``
          // fields, but keeping them is harmless.
        }
      : {};
    draft.setReport({
      sources: next,
      source: firstWithoutMeta,
    } as Record<string, unknown>);
  };

  const onSaveActive = (): void => {
    if (active == null) return;
    const next = [...sources];
    const nextName = name.trim() || active.name || `source_${activeIndex + 1}`;
    // A view has to name the table it materializes into: publish refuses
    // a view source without ``target`` rather than guess, because the
    // engine derives that name on its own and the legacy
    // ``duckdb_table`` field names a different table. Saving with the
    // field empty fills it with the same name the engine would derive
    // and shows it — visible and editable, never a silent default.
    const nextTarget = target.trim() || defaultViewTarget(nextName);
    next[activeIndex] = {
      ...active,
      name: nextName,
      target: nextTarget,
      sql: sql.trim(),
      description,
      kind: active.kind ?? "view",
    };
    setName(nextName);
    setTarget(nextTarget);
    writeSources(next);
  };

  const onAddSource = (): void => {
    const newName = nextSourceName(sources, draft.key);
    const newSource: ReportSource = {
      name: newName,
      kind: "view",
      sql: "",
      target: defaultViewTarget(newName),
      description: "",
    };
    writeSources([...sources, newSource]);
    setActiveIndex(sources.length);
  };

  const onDeleteActive = (): void => {
    if (active == null) return;
    if (sources.length === 1) {
      window.alert(
        t("editor.data.cantDeleteLast"),
      );
      return;
    }
    if (
      !window.confirm(
        t("editor.data.deleteConfirm", {
          name: active.name ?? `#${activeIndex + 1}`,
        }),
      )
    ) {
      return;
    }
    const next = sources.filter((_, i) => i !== activeIndex);
    writeSources(next);
    setActiveIndex(Math.max(0, activeIndex - 1));
  };

  // ── Apply: trigger backend recreate for THIS source ─────────────
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState<{ ok: boolean; message: string } | null>(
    null,
  );

  // Apply re-materializes what is SAVED in Postgres (recreate_report_view
  // reads report_config from the DB), not what is in this editor. So it
  // is blocked while the editor or the draft hold unsaved changes — no
  // auto-save, no SQL in the body: the admin saves, then applies.
  const applyBlocked =
    draft.dirty ||
    (active != null &&
      sourceEditsPending({ name, target, sql, description }, active));

  const onApplyActive = async (): Promise<void> => {
    if (!projectSlug || active == null || applyBlocked) return;
    setApplying(true);
    setApplyResult(null);
    try {
      // Phase 2 — pass source_name so only THIS source re-materializes
      // (cheaper than re-running every source of the report).
      const result = await projectsApi.dataSource.recreateView(
        projectSlug,
        draft.key,
        active.name ?? undefined,
      );
      setApplyResult({
        ok: result.error === null,
        message:
          result.error === null
            ? t("editor.data.viewApplied", {
                name: result.view_name,
              })
            : t("editor.data.viewApplyError", {
                name: result.view_name,
                err: result.error,
              }),
      });
      if (result.error === null) {
        await refresh();
      }
    } catch (err) {
      setApplyResult({
        ok: false,
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setApplying(false);
    }
  };

  if (sources.length === 0) {
    // Empty state: brand-new draft without any source declared.
    return (
      <div className="mx-auto max-w-2xl px-6 py-10">
        <Card>
          <CardBody className="space-y-3">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 shrink-0 text-warning-dark" size={20} />
              <div>
                <h3 className="text-sm font-semibold text-neutral-900">
                  {t("editor.data.noSourceTitle")}
                </h3>
                <p className="mt-1 text-xs text-neutral-600">
                  {t("editor.data.noSourceHint")}
                </p>
              </div>
            </div>
            <Button size="sm" onClick={onAddSource}>
              <Plus size={14} className="mr-1" />
              {t("editor.data.addSource")}
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    // Iter 27 follow-up — drop ``mx-auto max-w-6xl`` (was leaving a
    // big empty band on the left between the app sidebar and the data
    // sources card) and tighten the outer padding so the editor uses
    // the full content width. ``min-w-0`` on the flex children stops
    // the SQL textarea and preview table from forcing the page to
    // grow wider than the viewport (which produced the horizontal
    // scrollbar at the bottom of the canvas).
    <div className="w-full min-w-0 px-4 py-6">
      <div className="mb-4 flex items-center gap-2">
        <Database size={16} className="text-primary" />
        <h2 className="text-sm font-semibold text-neutral-900">
          {t("editor.data.title")}
        </h2>
        <span className="text-xs text-neutral-500">
          {t("editor.data.subtitle", {
            n: sources.length,
          })}
        </span>
      </div>

      <div className="flex min-w-0 flex-col gap-4 lg:flex-row">
        {/* ── Side-rail ─────────────────────────────── */}
        <aside className="shrink-0 lg:w-48">
          <nav
            className="flex flex-col gap-1 rounded-lg border border-neutral-200 bg-white p-2"
            aria-label={t("editor.data.nav.aria")}
          >
            {sources.map((s, i) => {
              const isActive = i === activeIndex;
              return (
                <button
                  key={s.name ?? i}
                  type="button"
                  onClick={() => setActiveIndex(i)}
                  className={
                    "flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs " +
                    (isActive
                      ? "bg-primary/10 font-semibold text-primary"
                      : "text-neutral-700 hover:bg-neutral-100")
                  }
                >
                  <FileCode size={12} className={isActive ? "text-primary" : "text-neutral-500"} />
                  <span className="truncate">
                    {s.name ?? `source ${i + 1}`}
                  </span>
                </button>
              );
            })}
            <button
              type="button"
              onClick={onAddSource}
              className="mt-1 flex items-center gap-2 rounded-md border border-dashed border-neutral-300 px-2 py-1.5 text-left text-xs text-neutral-600 hover:border-primary hover:text-primary"
            >
              <Plus size={12} />
              {t("editor.data.addSource")}
            </button>
          </nav>
        </aside>

        {/* ── Active source editor ──────────────────── */}
        <div className="min-w-0 flex-1 space-y-4">
          {active && (
            <>
              <Card>
                <CardBody className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileCode size={14} className="text-primary" />
                      <h3 className="text-xs font-semibold text-neutral-600">
                        {t("editor.data.source.editorTitle")}
                      </h3>
                    </div>
                    <button
                      type="button"
                      onClick={onDeleteActive}
                      className="flex items-center gap-1 text-xs text-danger hover:underline"
                    >
                      <Trash2 size={12} />
                      {t("editor.data.delete")}
                    </button>
                  </div>

                  {/* Name + target inputs ─────────────────────── */}
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <Input
                      label={t("editor.data.source.name")}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="fact_iego_weekly"
                      fullWidth
                      className="font-mono"
                    />
                    <Input
                      label={t("editor.data.source.target")}
                      helperText={t("editor.data.source.targetHint")}
                      value={target}
                      onChange={(e) => setTarget(e.target.value)}
                      placeholder="gating_fact_iego_weekly"
                      fullWidth
                      className="font-mono"
                    />
                  </div>

                  {/* SQL editor ─────────────────────────────── */}
                  <div>
                    {/* The SQL of a view is the single most important
                        thing anybody types on this screen, and it used to
                        be a bare <textarea>: no highlighting, no line
                        numbers, no copy button. The kit ships CodeEditor.
                        This was the largest single gap against the
                        reference in the whole audit (finding B14). */}
                    <CodeEditor
                      label={t("editor.data.source.sql")}
                      helperText={t("editor.data.source.sqlHint", {
                        target: target || "{target}",
                      })}
                      value={sql}
                      onValueChange={setSql}
                      language="sql"
                      placeholder="SELECT ... FROM project_raw_{slug} WHERE ..."
                      minHeight={260}
                      showLineNumbers
                      showCopyButton
                      fullWidth
                    />
                  </div>

                  {/* Description (Phase 4 — DDL re-home) ──────────── */}
                  <div>
                    <Textarea
                      label={t("editor.data.source.description")}
                      helperText={t("editor.data.source.descriptionHint")}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      spellCheck
                      rows={8}
                      fullWidth
                      placeholder="# fact_iego_weekly&#10;&#10;Weekly population-level metrics. Grain: week × country × marketplace × category."
                    />
                  </div>

                  {/* Save + Apply ─────────────────────────────── */}
                  <div className="flex flex-wrap items-center gap-2">
                    <Button size="sm" onClick={onSaveActive}>
                      <Save size={14} className="mr-1" />
                      {t("editor.data.save")}
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={!projectSlug || applying || applyBlocked}
                      onClick={() => void onApplyActive()}
                    >
                      {applying ? (
                        <Spinner
                          size={14}
                          color="currentColor"
                          className="mr-1"
                          ariaLabel={t("editor.data.apply")}
                        />
                      ) : (
                        <Play size={14} className="mr-1" />
                      )}
                      {t("editor.data.apply")}
                    </Button>
                    {applyResult && (
                      <span
                        className={
                          "text-xs " +
                          (applyResult.ok ? "text-success-dark" : "text-danger")
                        }
                      >
                        {applyResult.message}
                      </span>
                    )}
                    {applyBlocked && (
                      <span className="text-xs text-warning-dark">
                        {t("editor.data.applyNeedsSave")}
                      </span>
                    )}
                  </div>
                </CardBody>
              </Card>

              {/* Introspection preview ─────────────────────── */}
              <Card>
                <CardBody className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xs font-semibold text-neutral-600">
                      {t("editor.data.source.preview")}
                    </h3>
                    <div className="flex items-center gap-2">
                      <code className="rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-xs text-neutral-700">
                        {target || "—"}
                      </code>
                      {target && (
                        <button
                          type="button"
                          onClick={() => void refresh()}
                          disabled={introspectLoading}
                          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700"
                          title={t("editor.data.refreshTitle")}
                        >
                          <RefreshCw
                            size={10}
                            className={introspectLoading ? "animate-spin" : undefined}
                          />
                          {t("editor.data.refresh")}
                        </button>
                      )}
                    </div>
                  </div>
                  {introspectLoading && (
                    <div className="flex items-center gap-2 text-xs text-neutral-500">
                      <Spinner size={12} ariaLabel={t("editor.data.loading")} />
                      {t("editor.data.loading")}
                    </div>
                  )}
                  {introspectError && (
                    <p className="text-xs text-danger">{introspectError}</p>
                  )}
                  {!introspectLoading && !introspectError && columns == null && (
                    <p className="text-xs text-neutral-500">
                      {t("editor.data.source.notMaterialized", {
                        name: target || "—",
                      })}
                    </p>
                  )}
                  {columns && columns.length > 0 && (
                    <Disclosure
                      bare
                      summary={t("editor.data.source.schema", {
                        n: columns.length,
                      })}
                    >
                      <div className="mt-2 overflow-x-auto">
                        <table className="min-w-full text-xs">
                          <thead className="border-b border-neutral-200 text-left text-xs text-neutral-500">
                            <tr>
                              <th className="py-1 pr-2 font-medium">column</th>
                              <th className="py-1 pr-2 font-medium">type</th>
                            </tr>
                          </thead>
                          <tbody>
                            {columns.map((c) => (
                              <tr
                                key={c.name}
                                className="border-b border-neutral-100"
                              >
                                <td className="py-1 pr-2 font-mono">{c.name}</td>
                                <td className="py-1 pr-2 text-neutral-500">
                                  {c.type}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Disclosure>
                  )}

                  {/*
                    Iter 27 — Preview first N rows as a real table (was
                    a collapsed JSON dump prior). The data is already
                    fetched via ``duckdbApi.sample(target, 10)`` in the
                    refresh effect, so no new backend endpoint was
                    needed. Renders a striped HTML table with the first
                    ~6 columns visible (the rest accessible via
                    horizontal scroll).
                  */}
                  {sample && sample.length > 0 && (
                    <DataPreviewTable sample={sample} />
                  )}
                </CardBody>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Data preview helpers (iter 27) ──────────────────────────────────

interface DataPreviewTableProps {
  sample: ReadonlyArray<Record<string, unknown>>;
}

/**
 * Iter 27 — render the first N rows of the materialized table as a
 * real HTML table (striped, with horizontal scroll if wide). Replaces
 * the prior JSON-dump preview that hid the data behind a
 * ``<details>`` collapsed by default.
 *
 * Column order: keys of the first row in iteration order. DuckDB
 * preserves the SELECT column order so this matches what the engine
 * would see when querying the table.
 *
 * Values are formatted with ``formatCellValue`` semantics inline —
 * booleans and nulls render with hint colors, dates pass through as
 * ISO strings, numbers stay raw (no thousand separators) so the
 * admin can copy-paste into SQL.
 */
function DataPreviewTable({ sample }: DataPreviewTableProps): JSX.Element {
  const { t } = useT();
  const firstRow = sample[0] ?? {};
  const columns = Object.keys(firstRow);

  return (
    <div>
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold text-neutral-500">
          {t("editor.data.source.preview10", { n: sample.length })}
        </h4>
      </div>
      <div className="mt-2 overflow-x-auto rounded border border-neutral-200">
        <table className="min-w-full text-xs">
          <thead className="border-b border-neutral-200 bg-neutral-50 text-left text-xs text-neutral-600">
            <tr>
              {columns.map((col) => (
                <th
                  key={col}
                  className="whitespace-nowrap px-2 py-1.5 font-semibold"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sample.map((row, i) => (
              <tr
                key={i}
                className={
                  i % 2 === 0
                    ? "border-b border-neutral-100 bg-white"
                    : "border-b border-neutral-100 bg-neutral-50/50"
                }
              >
                {columns.map((col) => (
                  <td
                    key={col}
                    className="whitespace-nowrap px-2 py-1.5 font-mono text-xs text-neutral-800"
                  >
                    {formatPreviewCell(row[col])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Format a single DuckDB row value for the preview table. Honors the
 * "no fallbacks" rule: ``null`` and ``undefined`` are rendered
 * explicitly as muted placeholders so the admin sees them as
 * distinct from empty strings.
 */
function formatPreviewCell(value: unknown): string {
  if (value === null || value === undefined) {
    return "—";
  }
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}
